import type { IAssistantNavTarget } from '@/domain/assistant/assistant-nav-targets';
import type { IGhNavigateTarget } from '../gpthelp-host-contract';

/** Pages that open on the Menú (`useCatalogUrl` reads their query). */
export const MENU_PAGES = ['menu', 'menu-items'];

/** Where a card's navigate action goes, resolved against the member's pages. */
export interface IAssistantNavigation {
  path: string;
  query: Record<string, string>;
  /** Products to flash on the Menú page. */
  highlightItemIds: number[];
  /** The Menú category to open (`?cId=`, or its editor). */
  categoryId: number | null;
  /** The Menú product whose editor opens (`?item=`). */
  itemId: number | null;
}

const POSITIVE_INT = /^[1-9]\d{0,9}$/;

function positiveInt(raw: string | undefined): number | null {
  return raw && POSITIVE_INT.test(raw) ? Number(raw) : null;
}

/**
 * The Menú query of a card's params (`useCatalogUrl`): a product's editor,
 * a category's editor, a new product in a category, a new category, or a
 * jump to a category; plus the menu they are in.
 */
function menuQuery(
  params: Record<string, string>,
  itemId: number | null,
  categoryId: number | null
): Record<string, string> {
  const query: Record<string, string> = {};
  const editor = params.editor;
  if (itemId !== null) query.item = String(itemId);
  else if (editor === 'category' && categoryId !== null)
    query.category = String(categoryId);
  else if (editor === 'newProduct' && categoryId !== null) {
    query.item = 'new';
    query.cId = String(categoryId);
  } else if (editor === 'newCategory') query.category = 'new';
  // `useCatalogUrl` turns `cId` into a jump to the category.
  else if (categoryId !== null) query.cId = String(categoryId);

  const menuId = positiveInt(params.menuId);
  if (menuId !== null) query.mId = String(menuId);
  return query;
}

/**
 * Resolves a navigate target (plan §9.5) against the pages this member can
 * open now; null for a page they cannot (the card was built for another
 * member, store or plan).
 */
export function resolveAssistantNavigation(
  target: IGhNavigateTarget,
  navTargets: IAssistantNavTarget[]
): IAssistantNavigation | null {
  const page = navTargets.find((entry) => entry.key === target.page);
  if (!page) return null;

  const params = target.params ?? {};
  const onMenu = MENU_PAGES.includes(page.key);
  const itemId = onMenu ? positiveInt(params.itemId) : null;
  const categoryId = onMenu ? positiveInt(params.categoryId) : null;
  const query: Record<string, string> = {
    ...(page.query ?? {}),
    ...(onMenu ? menuQuery(params, itemId, categoryId) : {}),
  };

  const highlightItemIds = (target.highlight ?? [])
    .filter((id) => POSITIVE_INT.test(id))
    .map(Number);

  return { path: page.path, query, highlightItemIds, categoryId, itemId };
}
